import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Scale,
  Users,
  DollarSign,
  Calendar,
  TrendingUp,
  Plus,
  ArrowLeft,
  Clock,
  FileText,
  PartyPopper,
  CheckCircle,
} from "lucide-react";
import { BankOutlined, UserOutlined, HomeOutlined } from "@ant-design/icons";
import {
  dashboardService,
  casesService,
  sessionsService,
} from "../services/api.service";
import Layout from "../components/Layout";
import AddClientModal from "../components/clients/AddClientModal";
import AddCaseModal from "../components/case/AddCaseModal";
import AddSessionModal from "../components/sessions/AddSessionModal";
import AddPaymentModal from "../components/payments/addPayment";
import ViewSessionModel from "../components/sessions/ViewSessionModel";
import EditSessionModal from "../components/sessions/EditSessionModal";
import RegistrationWizard from "../components/registration/RegistrationWizard";
import { sessionTypes } from "../constants/sessionTypes";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { message } from "antd";
import logger from "../utils/logger";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentMonth] = useState(
    new Date().toLocaleString("en-US", { month: "long" })
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    stats: null,
    cases: [],
    events: [],
  });

  // التحقق من التسجيل الجديد
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const [newTenantInfo, setNewTenantInfo] = useState(null);

  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showAddCaseModal, setShowAddCaseModal] = useState(false);
  const [showAddSessionModal, setShowAddSessionModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [viewSessionModalVisible, setViewSessionModalVisible] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedSessionForEdit, setSelectedSessionForEdit] = useState(null);
  const [editSessionModalVisible, setEditSessionModalVisible] = useState(false);
  const [showRegistrationWizard, setShowRegistrationWizard] = useState(false);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      logger.debug("Fetching dashboard data...");

      // جلب البيانات بشكل متوازٍ لتحسين الأداء
      const [statsData, casesData, todaySessionsData] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getRecentCases(),
        sessionsService.getTodaySessions(),
      ]);

      logger.debug("Today's sessions fetched:", todaySessionsData);
      logger.debug("Number of today's sessions:", todaySessionsData.length);

      // تحسين معالجة بيانات الجلسات
      const processedSessions = await Promise.all(
        todaySessionsData.map(async (session) => {
          logger.debug("Processing session:", session.id);

          // إنشاء نسخة عميقة من الجلسة لتجنب تعديل الكائن الأصلي
          const processedSession = { ...session };

          // التأكد من وجود اسم العميل من مصادر متعددة
          if (!processedSession.client_name) {
            // محاولة جلب بيانات القضية إذا لم تكن متوفرة
            if (processedSession.case_id && !processedSession.case) {
              try {
                const caseDetails = await casesService.getCaseById(
                  processedSession.case_id
                );
                processedSession.case = caseDetails;
              } catch (error) {
                logger.error("Error fetching case details:", error);
              }
            }

            // التحقق من مصادر مختلفة لاسم العميل
            if (processedSession.case?.client_name) {
              processedSession.client_name = processedSession.case.client_name;
            } else if (processedSession.case?.client) {
              processedSession.client_name = processedSession.case.client;
            } else if (processedSession.case?.clients?.[0]?.name) {
              processedSession.client_name =
                processedSession.case.clients[0].name;
            } else if (processedSession.clients?.[0]?.name) {
              processedSession.client_name = processedSession.clients[0].name;
            } else if (processedSession.parties?.[0]?.name) {
              processedSession.client_name = processedSession.parties[0].name;
            } else {
              processedSession.client_name = "عميل غير محدد";
            }
          }

          // التأكد من وجود نوع الجلسة
          if (!processedSession.session_type) {
            if (processedSession.case?.session_type) {
              processedSession.session_type =
                processedSession.case.session_type;
            } else if (processedSession.case?.type) {
              processedSession.session_type = processedSession.case.type;
            } else {
              // استخدام نوع افتراضي إذا لم يتم العثور على نوع
              processedSession.session_type = "جلسة قضائية";
            }
          }

          // التأكد من وجود حالة الجلسة
          if (!processedSession.status) {
            // تحديد الحالة بناءً على الوقت
            const sessionTime =
              processedSession.time || processedSession.session_time;
            if (sessionTime) {
              const now = new Date();
              const [hours, minutes] = sessionTime.split(":").map(Number);
              const sessionDate = new Date(
                processedSession.date || processedSession.session_date || now
              );
              sessionDate.setHours(hours, minutes, 0, 0);

              if (sessionDate < now) {
                processedSession.status = "منعقدة";
              } else {
                processedSession.status = "قادمة";
              }
            } else {
              processedSession.status = "قادمة";
            }
          }

          // التأكد من وجود رقم القضية
          if (
            !processedSession.case_number &&
            processedSession.case?.case_number
          ) {
            processedSession.case_number = processedSession.case.case_number;
          }

          // التأكد من وجود معلومات المحكمة
          if (!processedSession.court && processedSession.case?.court_name) {
            processedSession.court = processedSession.case.court_name;
          }

          return processedSession;
        })
      );

      setDashboardData({
        stats: statsData,
        cases: casesData,
        events: processedSessions,
      });
    } catch (err) {
      logger.error("Error fetching dashboard data:", err);
      setError(err.message);
      message.error("فشل في تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // التحقق من التسجيل الجديد
    if (location.state?.isNewRegistration && location.state?.tenant) {
      setShowWelcomeMessage(true);
      setNewTenantInfo(location.state.tenant);

      // مسح البيانات من state لتجنب إعادة عرض الرسالة
      window.history.replaceState({}, document.title);

      // إخفاء الرسالة بعد 10 ثواني
      setTimeout(() => {
        setShowWelcomeMessage(false);
      }, 10000);
    }

    fetchDashboardData();
  }, [navigate, location]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">جاري التحميل...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle className="text-red-600">خطأ</CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </Layout>
    );
  }

  const quickActions = [
    {
      title: "تسجيل قيد جديد",
      icon: Plus,
      bgColor: "bg-indigo-50 hover:bg-indigo-100",
      textColor: "text-indigo-600",
      action: () => setShowRegistrationWizard(true),
    },
    {
      title: "إضافة عميل جديد",
      icon: Users,
      bgColor: "bg-green-50 hover:bg-green-100",
      textColor: "text-green-600",
      action: () => setShowAddClientModal(true),
    },
    {
      title: "إضافة قضية جديدة",
      icon: Scale,
      bgColor: "bg-blue-50 hover:bg-blue-100",
      textColor: "text-blue-600",
      action: () => setShowAddCaseModal(true),
    },
    {
      title: "إضافة جلسة",
      icon: FileText,
      bgColor: "bg-purple-50 hover:bg-purple-100",
      textColor: "text-purple-600",
      action: () => setShowAddSessionModal(true),
    },
    {
      title: "تسجيل مدفوعات",
      icon: DollarSign,
      bgColor: "bg-amber-50 hover:bg-amber-100",
      textColor: "text-amber-600",
      action: () => setShowAddPaymentModal(true),
    },
  ];

  const stats = [
    {
      title: "القضايا النشطة",
      value: dashboardData.stats?.activeCases || 0,
      icon: Scale,
      bgColor: "bg-emerald-500",
      iconColor: "text-emerald-600",
    },
    {
      title: "العملاء",
      value: dashboardData.stats?.totalClients || 0,
      icon: Users,
      bgColor: "bg-indigo-500",
      iconColor: "text-indigo-600",
    },
    {
      title: "المدفوعات المعلقة",
      value: dashboardData.stats?.pendingPayments || 0,
      icon: DollarSign,
      bgColor: "bg-amber-500",
      iconColor: "text-amber-600",
    },
    {
      title: "المواعيد القادمة",
      value: dashboardData.stats?.upcomingEvents || 0,
      icon: Calendar,
      bgColor: "bg-purple-500",
      iconColor: "text-purple-600",
    },
    {
      title: "إيرادات الشهر",
      value:
        dashboardData.stats?.monthlyRevenue > 0
          ? `${dashboardData.stats.monthlyRevenue.toLocaleString(
              "ar-SA"
            )} دينار`
          : "0 دينار",
      icon: TrendingUp,
      bgColor: "bg-red-500",
      iconColor: "text-red-600",
    },
  ];

  const getStatusBadgeVariant = (status) => {
    const variants = {
      نشط: "success",
      معلق: "warning",
      مكتمل: "info",
      ملغي: "destructive",
      مؤجل: "secondary",
    };
    return variants[status] || "default";
  };

  const getSessionTypeConfig = (sessionType) => {
    logger.debug("Session type:", sessionType);

    // إذا لم يتم تحديد نوع الجلسة، استخدم "جلسة أخرى"
    if (!sessionType) {
      logger.debug("No session type provided, using default");
      return sessionTypes["جلسة أخرى"];
    }

    // البحث عن نوع الجلسة
    let foundType = sessionTypes[sessionType];

    if (!foundType) {
      // البحث في قيم التسميات
      foundType = Object.values(sessionTypes).find(
        (type) => type.label === sessionType
      );
    }

    // إذا لم يتم العثور على نوع، استخدم النوع الافتراضي
    if (!foundType) {
      logger.debug("Session type not found, using default");
      foundType = sessionTypes["جلسة أخرى"];
    }

    logger.debug("Found session type:", foundType);
    return foundType;
  };

  // تعريف ألوان حالات الجلسات
  const getStatusColor = (status) => {
    logger.debug("Session status:", status);

    const colors = {
      منعقدة: "green",
      قادمة: "blue",
      مؤجلة: "orange",
      ملغية: "red",
      مكتملة: "green",
      معلقة: "purple",
    };

    // إذا لم يتم تحديد الحالة، استخدم "قادمة"
    if (!status) {
      logger.debug("No status provided, using default");
      return colors["قادمة"];
    }

    // البحث عن لون الحالة
    const color = colors[status] || "blue";
    logger.debug("Found status color:", color);
    return color;
  };

  // تعريف فئات CSS لحالات الجلسات
  const getStatusColorClass = (status) => {
    const colorClasses = {
      منعقدة: "border-green-500 text-green-700 bg-green-50",
      قادمة: "border-blue-500 text-blue-700 bg-blue-50",
      مؤجلة: "border-orange-500 text-orange-700 bg-orange-50",
      ملغية: "border-red-500 text-red-700 bg-red-50",
      مكتملة: "border-green-500 text-green-700 bg-green-50",
      معلقة: "border-purple-500 text-purple-700 bg-purple-50",
    };

    // إذا لم يتم تحديد الحالة، استخدم فئة "قادمة"
    return colorClasses[status] || colorClasses["قادمة"];
  };

  const handleSelectSession = (session) => {
    logger.debug("Selected session:", session);
    logger.debug("Session type in handleSelectSession:", session.session_type);
    logger.debug("Client name in handleSelectSession:", session.client_name);

    // استخدام البيانات المحسنة مسبقًا من الجلسة
    setSelectedSession(session);
    setViewSessionModalVisible(true);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50/30 px-4 py-6 md:p-8" dir="rtl">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Welcome Message for New Registration */}
          {showWelcomeMessage && newTenantInfo && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-xl p-6 shadow-lg relative overflow-hidden"
            >
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-100 rounded-full -mr-16 -mt-16 opacity-50"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-100 rounded-full -ml-12 -mb-12 opacity-50"></div>

              <button
                onClick={() => setShowWelcomeMessage(false)}
                className="absolute top-4 left-4 text-gray-400 hover:text-gray-600 transition"
              >
                ✕
              </button>

              <div className="flex items-start gap-4 relative z-10">
                <div className="bg-green-500 p-3 rounded-full flex-shrink-0">
                  <PartyPopper className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    🎉 مرحباً بك في نظام إدارة مكاتب المحاماة!
                  </h3>
                  <p className="text-gray-700 mb-4">
                    تم إنشاء حسابك <strong>{newTenantInfo.name}</strong> بنجاح!
                    أنت الآن جاهز لبدء تنظيم وإدارة مكتبك بكل سهولة.
                  </p>

                  <div className="bg-white rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      ابدأ الآن في 3 خطوات سهلة:
                    </h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-start gap-2">
                        <span className="font-bold text-green-600">1.</span>
                        <span>أضف عملائك من خلال زر "إضافة عميل" أدناه</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-bold text-green-600">2.</span>
                        <span>سجّل القضايا والملفات الخاصة بكل عميل</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-bold text-green-600">3.</span>
                        <span>جدول الجلسات وتتبع المدفوعات بسهولة</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => setShowAddClientModal(true)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2 text-sm font-semibold"
                    >
                      <Plus className="w-4 h-4" />
                      إضافة أول عميل
                    </button>
                    <button
                      onClick={() => setShowWelcomeMessage(false)}
                      className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm"
                    >
                      سأبدأ لاحقاً
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 mt-3">
                    💡 نصيحة: يمكنك الوصول لجميع الميزات من القائمة الجانبية
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Header Section */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">لوحة التحكم</h1>
              <p className="text-sm text-gray-600 mt-1">{currentMonth}</p>
            </div>
          </div>
          {/* Quick Actions Section */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-700">
              إجراءات سريعة
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {quickActions.map((action, index) => {
                const IconComponent = action.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Button
                      onClick={action.action}
                      variant="outline"
                      className={`w-full h-auto ${action.bgColor} ${action.textColor} 
                        p-6 rounded-xl transition-all duration-200 
                        flex flex-col items-center justify-center gap-3
                        hover:shadow-lg active:scale-95 min-h-[140px]
                        border border-gray-200`}
                    >
                      <IconComponent className="h-8 w-8" />
                      <span className="font-medium text-center text-sm">
                        {action.title}
                      </span>
                    </Button>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Statistics Overview Section */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-700">نظرة عامة</h2>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {stats.map((stat, index) => {
                const IconComponent = stat.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="hover:shadow-md transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-gray-600">
                              {stat.title}
                            </p>
                            <p
                              className={`text-2xl font-bold ${stat.iconColor}`}
                            >
                              {stat.value}
                            </p>
                          </div>
                          <div
                            className={`${stat.bgColor} bg-opacity-10 p-3 rounded-full`}
                          >
                            <IconComponent
                              className={`h-6 w-6 ${stat.iconColor}`}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Recent Activity Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
            {/* Recent Cases */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-2"
            >
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-xl">آخر القضايا</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate("/cases")}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <span className="ml-2">عرض الكل</span>
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {dashboardData.cases.slice(0, 3).map((caseItem, index) => (
                    <motion.div
                      key={caseItem.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="group flex items-center justify-between p-4 hover:bg-blue-50 rounded-xl transition-all duration-200 border border-gray-100 hover:border-blue-200 cursor-pointer"
                      onClick={() => navigate(`/cases/${caseItem.id}`)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-full bg-blue-100 group-hover:bg-blue-200 transition-colors duration-200">
                          <Scale className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-800">
                            {caseItem.case_number}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {caseItem.client_name}
                          </p>
                        </div>
                      </div>
                      <div className="text-left space-y-2">
                        <p className="text-sm text-gray-600">
                          {new Date(caseItem.filing_date).toLocaleDateString(
                            "en-Uk"
                          )}
                        </p>
                        <Badge variant={getStatusBadgeVariant(caseItem.status)}>
                          {caseItem.status}
                        </Badge>
                      </div>
                    </motion.div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>

            {/* Today's Schedule */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-xl">جلسات اليوم</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate("/calendar")}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <span className="ml-2">التقويم</span>
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : dashboardData.events.length > 0 ? (
                    dashboardData.events.map((event, index) => {
                      logger.debug("Event data:", event);
                      logger.debug("Event session_type:", event.session_type);
                      logger.debug("Event client_name:", event.client_name);

                      // التحقق من اسم العميل من مصادر مختلفة
                      const clientName =
                        event.client_name ||
                        event.case?.client_name ||
                        event.case?.client ||
                        event.case?.clients?.[0]?.name ||
                        event.clients?.[0]?.name ||
                        event.parties?.[0]?.name ||
                        "عميل غير محدد";

                      // التحقق من نوع الجلسة من مصادر مختلفة
                      const sessionTypeValue =
                        event.session_type ||
                        event.case?.session_type ||
                        event.case?.type ||
                        event.type ||
                        "جلسة قضائية";

                      logger.debug("Session type value:", sessionTypeValue);
                      logger.debug("Client name:", clientName);
                      const sessionType =
                        getSessionTypeConfig(sessionTypeValue);

                      // تنسيق التاريخ الميلادي
                      const sessionDate = event.date || event.session_date;
                      const formattedDate = sessionDate
                        ? new Date(sessionDate).toLocaleDateString("en-Uk")
                        : "غير محدد";

                      return (
                        <motion.div
                          key={event.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="group bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 cursor-pointer"
                          onClick={() => handleSelectSession(event)}
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-2">
                              <Badge
                                variant={sessionType.variant}
                                className="text-xs px-2 py-1"
                              >
                                {sessionType.label}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={`text-xs px-2 py-1 ${getStatusColorClass(
                                  event.status || "قادمة"
                                )}`}
                              >
                                {event.status || "قادمة"}
                              </Badge>
                            </div>
                            <div className="text-sm text-gray-500 font-medium">
                              {formattedDate}
                            </div>
                          </div>

                          <h3 className="font-semibold text-gray-800 mb-3 text-base">
                            {event.title ||
                              `جلسة - ${event.case_number || "بدون رقم قضية"}`}
                          </h3>

                          {/* عرض اسم العميل */}
                          <div className="flex items-center gap-2 bg-blue-50 p-2 rounded-md mb-3">
                            <UserOutlined className="h-4 w-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-900">
                              {clientName}
                            </span>
                          </div>

                          <div className="space-y-2 mb-3">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Clock className="h-4 w-4 text-blue-500" />
                              <span>
                                الوقت:{" "}
                                {event.time || event.session_time || "غير محدد"}
                              </span>
                            </div>

                            {event.court && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <BankOutlined className="h-4 w-4 text-blue-500" />
                                <span>المحكمة: {event.court}</span>
                              </div>
                            )}

                            {event.room && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <HomeOutlined className="h-4 w-4 text-blue-500" />
                                <span>القاعة: {event.room}</span>
                              </div>
                            )}
                          </div>

                          {event.notes && (
                            <div className="bg-gray-50 p-2 rounded text-xs text-gray-600 mb-3">
                              <p>{event.notes}</p>
                            </div>
                          )}

                          <div className="flex justify-end items-center text-blue-500 text-sm font-medium">
                            <span>عرض التفاصيل</span>
                            <ArrowLeft className="h-4 w-4 mr-1 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </motion.div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                      <p>لا توجد جلسات اليوم</p>
                      <Button
                        variant="outline"
                        className="mt-4"
                        onClick={() => setShowAddSessionModal(true)}
                      >
                        <Plus className="h-4 w-4 ml-2" />
                        إضافة جلسة جديدة
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Modals */}
          {showAddCaseModal && (
            <AddCaseModal
              visible={showAddCaseModal}
              onClose={() => setShowAddCaseModal(false)}
              onSuccess={(data) => {
                // AddCaseModal الآن يتعامل مع إنشاء القضية بنفسه
                // نحن فقط نحتاج لتحديث البيانات وإغلاق modal
                setShowAddCaseModal(false);
                fetchDashboardData();
                // لا حاجة لرسالة نجاح هنا لأن AddCaseModal يتعامل مع ذلك
              }}
            />
          )}

          {showAddClientModal && (
            <AddClientModal
              visible={showAddClientModal}
              onClose={() => setShowAddClientModal(false)}
              onSuccess={() => {
                setShowAddClientModal(false);
                // Refresh dashboard data
                fetchDashboardData();
              }}
            />
          )}

          {showAddSessionModal && (
            <AddSessionModal
              visible={showAddSessionModal}
              onClose={() => {
                setShowAddSessionModal(false);
                setSelectedSessionForEdit(null);
              }}
              onSuccess={() => {
                setShowAddSessionModal(false);
                setSelectedSessionForEdit(null);
                fetchDashboardData();
              }}
              defaultSessionType="جلسة قضائية"
              editMode={!!selectedSessionForEdit}
              initialData={selectedSessionForEdit}
            />
          )}

          {showAddPaymentModal && (
            <AddPaymentModal
              visible={showAddPaymentModal}
              onClose={() => setShowAddPaymentModal(false)}
              onSuccess={() => {
                setShowAddPaymentModal(false);
                fetchDashboardData();
              }}
            />
          )}

          <ViewSessionModel
            visible={viewSessionModalVisible}
            onClose={() => setViewSessionModalVisible(false)}
            sessionData={selectedSession}
            onEdit={(sessionData) => {
              // إغلاق نافذة عرض التفاصيل
              setViewSessionModalVisible(false);
              // فتح نافذة تعديل الجلسة مع البيانات
              setEditSessionModalVisible(true);
              // تعيين بيانات الجلسة الحالية كبيانات افتراضية للتعديل
              setSelectedSessionForEdit(sessionData);
            }}
          />

          {editSessionModalVisible && (
            <EditSessionModal
              visible={editSessionModalVisible}
              onClose={() => {
                setEditSessionModalVisible(false);
                setSelectedSessionForEdit(null);
              }}
              onSuccess={() => {
                setEditSessionModalVisible(false);
                setSelectedSessionForEdit(null);
                fetchDashboardData();
                message.success("تم تحديث الجلسة بنجاح");
              }}
              sessionData={selectedSessionForEdit}
            />
          )}
          
          {showRegistrationWizard && (
            <RegistrationWizard
              visible={showRegistrationWizard}
              onClose={() => setShowRegistrationWizard(false)}
              onSuccess={() => {
                setShowRegistrationWizard(false);
                fetchDashboardData();
              }}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
